import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Ellipsis, LayoutGrid, Star } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { createMenuVisibilityContext, getMenuLabel } from '../lib/menu'
import { collectOverviewSections, MENU_OVERVIEW_PATH } from '../lib/menu/menuOverviewSections'
import { resolveFavoriteMenuItems } from '../lib/menu/menuFavoriteResolution'
import { useMenuFavoritesQuery } from '../lib/menu/menuFavorites'
import MenuIcon from './MenuIcon'

function resolveDirectFavoriteCount() {
  if (typeof window === 'undefined') return 3
  if (window.innerWidth >= 2200) return 8
  if (window.innerWidth >= 2000) return 7
  if (window.innerWidth >= 1800) return 6
  if (window.innerWidth >= 1600) return 5
  if (window.innerWidth >= 1440) return 4
  if (window.innerWidth >= 1280) return 3
  return 0
}

function isPathActive(pathname, itemPath) {
  if (!itemPath) return false
  const pathOnly = itemPath.split('?')[0]
  if (pathOnly === '/') return pathname === '/'
  return pathname === pathOnly || pathname.startsWith(`${pathOnly}/`)
}

function resolveActiveFavoriteMenuId(pathname, items) {
  return (items || [])
    .filter((item) => isPathActive(pathname, item.path))
    .sort((a, b) => b.path.split('?')[0].length - a.path.split('?')[0].length)[0]?.id || null
}

function FavoriteLink({ item, active, compact = false, onClick }) {
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={compact
        ? `inline-flex h-8 min-w-0 max-w-[9rem] items-center gap-1.5 rounded border px-2.5 text-xs font-semibold transition-colors ${
            active
              ? 'border-sky-300 bg-sky-50 text-brand-navy'
              : 'border-slate-200 bg-white text-slate-700 hover:border-sky-200 hover:bg-sky-50'
          }`
        : `flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium transition-colors ${
            active ? 'bg-sky-50 text-brand-navy' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'
          }`}
    >
      <MenuIcon menuItemId={item.id} emoji={item.icon} className="shrink-0 text-slate-500" size={compact ? 16 : 18} />
      <span className="min-w-0 truncate">{getMenuLabel(item)}</span>
    </Link>
  )
}

export default function HeaderMenuFavorites() {
  const { user, hasAnyRole, hasAnyPermission, hasAnyCapability } = useAuth()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const [directFavoriteCount, setDirectFavoriteCount] = useState(resolveDirectFavoriteCount)
  const rootRef = useRef(null)
  const favoritesQuery = useMenuFavoritesQuery({ enabled: Boolean(user) })

  const menuCtx = useMemo(
    () => createMenuVisibilityContext(user, { hasAnyRole, hasAnyPermission, hasAnyCapability }),
    [user, hasAnyRole, hasAnyPermission, hasAnyCapability],
  )
  const blocks = useMemo(() => collectOverviewSections(menuCtx), [menuCtx])
  const favoriteItems = useMemo(
    () => resolveFavoriteMenuItems(favoritesQuery.data, blocks),
    [favoritesQuery.data, blocks],
  )
  const activeFavoriteMenuId = useMemo(
    () => resolveActiveFavoriteMenuId(pathname, favoriteItems),
    [pathname, favoriteItems],
  )
  const directItems = favoriteItems.slice(0, directFavoriteCount)
  const overflowItems = favoriteItems.slice(directFavoriteCount)

  useEffect(() => {
    function handleResize() {
      setDirectFavoriteCount(resolveDirectFavoriteCount())
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!open) return undefined
    function handlePointerDown(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false)
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  if (!favoriteItems.length) {
    return (
      <div className="flex min-w-0 flex-1 items-center">
        <Link
          to={MENU_OVERVIEW_PATH}
          className="inline-flex h-9 w-9 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-brand-navy xl:w-auto xl:gap-1.5 xl:px-2.5 xl:text-xs xl:font-semibold"
          title="즐겨찾기 설정"
          aria-label="즐겨찾기 설정"
        >
          <Star className="h-4 w-4" aria-hidden="true" />
          <span className="hidden xl:inline">즐겨찾기 설정</span>
        </Link>
      </div>
    )
  }

  return (
    <div ref={rootRef} className="relative flex min-w-0 flex-1 items-center">
      <button
        type="button"
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded text-slate-600 hover:bg-slate-100 hover:text-brand-navy xl:hidden"
        aria-label="즐겨찾기 메뉴 열기"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Star className="h-4 w-4 fill-amber-400 text-amber-500" aria-hidden="true" />
      </button>

      <div className="hidden min-w-0 items-center gap-1.5 xl:flex">
        {directItems.map((item) => (
          <FavoriteLink key={item.id} item={item} active={item.id === activeFavoriteMenuId} compact />
        ))}
        {overflowItems.length ? (
          <button
            type="button"
            className="inline-flex h-8 shrink-0 items-center gap-1 rounded px-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            aria-label={`즐겨찾기 메뉴 ${overflowItems.length}개 더 보기`}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            <Ellipsis className="h-4 w-4" aria-hidden="true" />
            <span>{overflowItems.length}</span>
          </button>
        ) : null}
      </div>

      {open ? (
        <div
          role="menu"
          className="absolute left-0 top-[calc(100%+0.65rem)] z-50 w-64 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          <div className="border-b border-slate-100 px-3 py-2 text-xs font-bold text-slate-500">즐겨찾기</div>
          <div className="max-h-[min(60vh,24rem)] overflow-y-auto xl:hidden">
            {favoriteItems.map((item) => (
              <FavoriteLink key={item.id} item={item} active={item.id === activeFavoriteMenuId} onClick={() => setOpen(false)} />
            ))}
          </div>
          <div className="hidden max-h-[min(60vh,24rem)] overflow-y-auto xl:block">
            {overflowItems.map((item) => (
              <FavoriteLink key={item.id} item={item} active={item.id === activeFavoriteMenuId} onClick={() => setOpen(false)} />
            ))}
          </div>
          <Link
            to={MENU_OVERVIEW_PATH}
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 border-t border-slate-100 px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <LayoutGrid className="h-4 w-4 text-slate-500" aria-hidden="true" />
            즐겨찾기 관리
          </Link>
        </div>
      ) : null}
    </div>
  )
}
